import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";

export default function PlansCard() {
  const [loading, setLoading] = useState(true);

  // contagem real
  const [counts, setCounts] = useState({
    free: 0,
    plus: 0,
    premium: 0,
    diamond: 0,
  });

  // preços/nome vindos da coleção planos
  const [planInfo, setPlanInfo] = useState({
    plus: { nome: "One Love Plus", preco: 14.9, id: null },
    premium: { nome: "One Love Premium", preco: 59.9, id: null },
    diamond: { nome: "One Love Diamond", preco: 349.0, id: null },
  });

  // ============================================================
  // Helpers
  // ============================================================
  const normalizeKeyFromText = (textRaw) => {
    const text = String(textRaw || "").toLowerCase().trim();

    // ✅ Aqui NÃO usamos "anual/mensal" porque hoje seus planos são Plus/Premium/Diamond
    // Isso evita classificar usuário antigo como diamond/premium sem querer.
    if (text.includes("diamond")) return "diamond";
    if (text.includes("premium")) return "premium";
    if (text.includes("plus")) return "plus";

    return null;
  };

  const normalizeUserPlan = (userData, planosMapById, planosMapByNameKey) => {
    // 1) ✅ prioridade total: campo `plan` já normalizado pelo seu painel
    const planField = String(userData?.plan || "").toLowerCase().trim();
    if (["free", "plus", "premium", "diamond"].includes(planField)) {
      return planField;
    }

    // 2) ✅ se tiver planoAtual (id do doc em /planos), resolve por ID
    const planoAtualId = String(userData?.planoAtual || "").trim();
    if (planoAtualId && planosMapById[planoAtualId]) {
      const keyFromPlanDoc = planosMapById[planoAtualId]?.key;
      if (keyFromPlanDoc) return keyFromPlanDoc;
    }

    // 3) ✅ fallback por nome
    const planoNome = String(userData?.planoNome || "").trim();
    const keyFromName = normalizeKeyFromText(planoNome);
    if (keyFromName) return keyFromName;

    // 4) se nada bater, é free
    return "free";
  };

  const formatMoney = (v) =>
    Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

  // ============================================================
  // Load (planos + users)
  // ============================================================
  useEffect(() => {
    async function load() {
      try {
        // 1) Carrega planos
        const planosSnap = await getDocs(collection(db, "planos"));

        // Vamos montar:
        // - map por ID: { [id]: { key, nome, preco } }
        // - map por key: { plus/premium/diamond: { nome, preco, id } }
        const planosMapById = {};
        const planosMapByKey = {
          plus: { nome: "One Love Plus", preco: 14.9, id: null },
          premium: { nome: "One Love Premium", preco: 59.9, id: null },
          diamond: { nome: "One Love Diamond", preco: 349.0, id: null },
        };

        planosSnap.forEach((docSnap) => {
          const p = docSnap.data();
          const nome = String(p?.nome || "").trim();
          const preco = Number(p?.preco || 0);

          const key = normalizeKeyFromText(nome); // plus|premium|diamond|null
          if (!key) return;

          planosMapById[docSnap.id] = { key, nome, preco, id: docSnap.id };
          planosMapByKey[key] = { nome, preco, id: docSnap.id };
        });

        setPlanInfo(planosMapByKey);

        // 2) Carrega users
        const usersSnap = await getDocs(collection(db, "users"));

        let free = 0;
        let plus = 0;
        let premium = 0;
        let diamond = 0;

        usersSnap.forEach((docSnap) => {
          const u = docSnap.data();

          const key = normalizeUserPlan(u, planosMapById, planosMapByKey);

          if (key === "free") free++;
          else if (key === "plus") plus++;
          else if (key === "premium") premium++;
          else if (key === "diamond") diamond++;
          else free++;
        });

        setCounts({ free, plus, premium, diamond });
      } catch (e) {
        console.error("Erro ao carregar distribuição de planos:", e);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  const percentages = useMemo(() => {
    const total = counts.free + counts.plus + counts.premium + counts.diamond;
    const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

    return {
      total,
      free: pct(counts.free),
      plus: pct(counts.plus),
      premium: pct(counts.premium),
      diamond: pct(counts.diamond),
    };
  }, [counts]);

  if (loading) {
    return (
      <div className="metric-card">
        <p className="metric-label">Planos</p>
        <p className="metric-value">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="metric-card">
      <p className="metric-label">Planos</p>

      <div className="metric-list">
        <div className="metric-list-item">Free: {percentages.free}%</div>

        <div className="metric-list-item">
          Plus (R${formatMoney(planInfo.plus.preco)}): {percentages.plus}%
        </div>

        <div className="metric-list-item">
          Premium (R${formatMoney(planInfo.premium.preco)}): {percentages.premium}%
        </div>

        <div className="metric-list-item">
          Diamond (R${formatMoney(planInfo.diamond.preco)}): {percentages.diamond}%
        </div>
      </div>

      <span className="metric-tag">Proporção por tipo de plano</span>
    </div>
  );
}
